import type { MtmWidgetProfile } from './types';
import { assertMtmWidgetProfileComplete } from './mtmWidgetProfileValidation';
import { buildMtmWidgetSessionState } from './buildMtmWidgetSessionState';
import type { MtmWidgetSession } from './mtmWidgetSession';

const SAIA_API_BASE = 'https://saia.3dlook.me/api/v2';
const MTM_METADATA_STATUS = 'set metadata';

function isValidEmail(value: string | null | undefined) {
  return Boolean(value && value.length > 3 && value.includes('@'));
}

function readApiErrorMessage(payload: unknown, fallback: string) {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const detail = record.detail ?? record.message ?? record.error;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  return fallback;
}

async function parseApiResponse(response: Response, fallbackMessage: string) {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(readApiErrorMessage(payload, fallbackMessage));
  }

  return payload;
}

async function fetchWithRetry(url: string, init: RequestInit, fallbackMessage: string): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(url, init);
      return response;
    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error(fallbackMessage);
      if (attempt === 0) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
      }
    }
  }

  throw lastError ?? new Error(fallbackMessage);
}

export async function syncMtmWidgetSessionMetadata(
  session: MtmWidgetSession,
  profile: MtmWidgetProfile,
): Promise<void> {
  assertMtmWidgetProfileComplete(profile);

  const email = profile.defaultValues.email ?? profile.userContext?.email ?? '';
  const firstName = profile.firstName?.trim() || null;

  if (!isValidEmail(email)) {
    throw new Error('Email is required before syncing 3DLOOK metadata');
  }

  const widgetState = buildMtmWidgetSessionState(profile, {
    status: MTM_METADATA_STATUS,
    processStatus: '',
    mtmClientId: session.mtmClientId,
  });

  await parseApiResponse(
    await fetchWithRetry(
      `${SAIA_API_BASE}/persons/widget/${session.uuid}/`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `UUID ${session.uuid}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unit: 'cm',
          email,
          ...(firstName ? { firstName } : {}),
          mtm_client: session.mtmClientId,
          state: widgetState,
        }),
      },
      'Failed to sync 3DLOOK widget profile',
    ),
    'Failed to sync 3DLOOK widget profile',
  );

  await parseApiResponse(
    await fetchWithRetry(
      `${SAIA_API_BASE}/measurements/mtm-clients/${session.mtmClientId}/`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `UUID ${session.uuid}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unit: 'cm',
          email,
          ...(firstName ? { first_name: firstName } : {}),
        }),
      },
      'Failed to sync 3DLOOK MTM client profile',
    ),
    'Failed to sync 3DLOOK MTM client profile',
  );
}
